/**
 * Adapts the flat `PublicRosterRow[]` a public share-view payload carries
 * into `LiveVideoRow[]` — the shape `LiveContentTab.tsx`'s columns read.
 *
 * Unlike its Scripts/Content siblings (which adapt into a placeholder shape
 * "structurally close to" the authed BE row type and cast with
 * `as unknown as RosterEntryX[]`), `LiveContentTab.tsx` feeds
 * `DataTable<LiveVideoRow>` directly — `LiveVideoRow` (LiveContentTab.rows.ts)
 * is already a purpose-built display row, not the raw `RosterEntryLive` wire
 * shape, so this adapter targets it with no unsafe array cast.
 *
 * A SIBLING of Content's (`ContentTab/publicRosterRowAdapter.ts`) — same BE
 * builder path (`buildProductionRosterView`), same flat wire shape.
 * `LiveVideoRow` has no `snapshot_metrics`/`deliverables` field either, so
 * those two shared helpers don't apply here (same as Scripts/Content).
 *
 * **Why `source`/`title`/thumbnail/stats are frozen, never-real placeholders:**
 * the wire carries only identity (username/name/avatar_url) + `live_url` — no
 * live_video statistics, thumbnail, title, or content_kind at all (those live
 * on `RosterEntryLive.live_video`, which `buildPublicRosterRow` never
 * populates for any tab). So every `LiveVideoRow` field besides
 * `creator`/`url` here is a placeholder that satisfies the type only —
 * nothing reads it for real rendering. `LiveContentTab.tsx`'s Status / Views /
 * Likes / Cmts / Content cells all force a permanent skeleton off the
 * component-level `isPublicView` flag, never off these values. `source` is
 * frozen to `'live_pending'` for the same reason Content's `status` is frozen
 * to a single literal: the wire has no field for the real per-row production
 * phase, so a specific, always-the-same placeholder can never look like real
 * data if some future change ever read it directly instead of going through
 * the skeleton gate.
 *
 * **Why `url` is DIFFERENT and passes `live_url` straight through (mirrors
 * `ContentTab.publicRosterRowAdapter.ts`'s identical `live_url` reasoning):**
 * unlike `source`/`title`/stats (no wire field at all), `url` genuinely IS the
 * same field, with the same meaning, on both the wire
 * (`PublicRosterRow.live_url`) and — once published — the authed
 * `RosterEntryLive.live_video.url` this table ultimately renders. It is
 * documented (Tasks 16/18/19's reviews) as ALWAYS EMPTY on the wire TODAY — a
 * known, pre-existing BE gap (the `live_videos` collection is out of scope
 * for this feature), not a mapping problem — so passing it through is
 * correct now (naturally always `undefined` → `''`) and stays correct the day
 * the BE gap closes, with no adapter change needed.
 *
 * **Defense-in-depth (this is THE tab where it matters most — Live-Content is
 * the one place a real published-video URL would actually show up):**
 * nothing in `LiveContentTab.tsx` reads `row.url` for real in public mode
 * regardless — the Content column's cell (the only place `url` drives
 * anything: row clickability + the video-popup source) is forced to a
 * permanent skeleton off the component-level `isPublicView` flag BEFORE any
 * branch reads `row.source` or `row.url`, not merely after happening to find
 * them empty. So even though this adapter passes a real `live_url` value
 * through whenever the wire carries one, the gate holds independent of that
 * value — see `LiveContentTab.publicView.test.tsx`'s dedicated regression
 * test, which fixtures a non-empty `live_url` and asserts the Content cell
 * still renders only a skeleton, never a link or a clickable row.
 */
import type { PublicRosterRow } from '@discovr/features';
import type { LiveVideoRow } from './LiveContentTab.rows';
import { adaptPublicRosterRowIdentity } from './publicRosterRowSharedFields';
import { rowDisplayName } from '../utils/rowDisplayName';

function adaptPublicRosterRow(row: PublicRosterRow): LiveVideoRow {
  const identity = adaptPublicRosterRowIdentity(row);
  return {
    key: identity.id,
    creatorId: identity.id,
    creatorName: rowDisplayName(identity),
    // Frozen placeholder — see this file's header. Never read for real
    // rendering: LiveContentTab.tsx's Status/Content cells force a skeleton
    // off `isPublicView`, not off this value.
    source: 'live_pending',
    title: '',
    // Genuinely the same field, passed straight through — see this file's
    // header for why this one (unlike source/title/stats) isn't frozen.
    url: row.live_url ?? '',
    thumbnail: undefined,
    contentKind: undefined,
    viewCount: undefined,
    likeCount: undefined,
    commentCount: undefined,
    rawViewCount: undefined,
    rawLikeCount: undefined,
    rawCommentCount: undefined,
    uploadDate: undefined,
    embedId: null,
    creator: identity as unknown as Record<string, unknown>,
    assignedProducts: undefined,
  };
}

export function adaptPublicRosterRows(rows: PublicRosterRow[]): LiveVideoRow[] {
  return rows.map(adaptPublicRosterRow);
}

/** Named alias matching the sibling adapters' call-site naming convention
 *  (`adaptPublicRosterRowsAsContentRows` / `...ScriptRows` / `...FinalizedRows`
 *  / `...CreatorsRows`) even though no cast is needed here — `LiveVideoRow`
 *  IS the real target type `LiveContentTab.tsx`'s `DataTable` consumes. */
export const adaptPublicRosterRowsAsLiveVideoRows = adaptPublicRosterRows;
